import React, { useCallback, useEffect, useMemo } from "react";
import { ProductPriceLine, useUpdateOrCreateProductPriceLineMutation } from "../api/productPriceLineApi";
import { SalesAreaPriceLine } from "../api/priceLineApi";
import { HookTextField, useHookForm } from "mui-react-hook-form-plus";
import _ from "lodash";

export type ProductPriceLineForm = {
  price: string;
};
export default function ProductPriceLineEditor({
  salesAreaPriceLine,
  productPriceLines,
  productId,
  currency,
}: {
  salesAreaPriceLine: SalesAreaPriceLine;
  productPriceLines: ProductPriceLine[];
  productId: number;
  currency: string;
}) {
  const productPriceLine = _.chain(productPriceLines)
    .find((productPriceLine) => productPriceLine.sales_area_price_line_id == salesAreaPriceLine.id)
    .value();

  const hookForm = useHookForm<ProductPriceLineForm>({
    defaultValues: {
      price: productPriceLine ? String(productPriceLine.price_in_cents / 100.0) : "",
    },
    reValidateMode: "onChange",
  });
  const { registerState, handleSubmit, getValues, setValue, formState } = hookForm;

  const [updateOrCreate] = useUpdateOrCreateProductPriceLineMutation();

  useEffect(() => {
    setValue("price", productPriceLine ? String(productPriceLine.price_in_cents / 100.0) : "");
  }, [productPriceLine, setValue]);

  const onSubmit = useCallback(() => {
    hookForm.trigger().then((result) => {
      if (result) {
        updateOrCreate({
          sales_area_id: salesAreaPriceLine.sales_area_id,
          sales_area_price_line_id: salesAreaPriceLine.id,
          price_in_cents: getValues("price") == "" ? null : Math.round(Number(getValues("price")) * 100),
          product_id: productId,
        });
      }
    });
  }, [hookForm, updateOrCreate, salesAreaPriceLine.sales_area_id, salesAreaPriceLine.id, getValues, productId]);

  const currencySymbol = useMemo(() => {
    const formatter = new Intl.NumberFormat("nl-NL", {
      style: "currency",
      currency: currency,
      currencyDisplay: "symbol",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

    return formatter.format(0).replace(/\d/g, "");
  }, [currency]);

  return (
    <HookTextField
      textFieldProps={{
        variant: "outlined",
        label: salesAreaPriceLine.title,
        size: "small",

        sx: { width: 100 },
        InputProps: { startAdornment: currencySymbol },
        onBlur: () => {
          onSubmit();
        },
      }}
      {...registerState("price")}
      rules={{
        pattern: { value: /^(\d*(\.\d+)?)?$/, message: "A number is only allowed" },
      }}
    />
  );
}
